import { useEffect, useState } from 'react';
import { Calendar, MapPin, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const AWSLambdaEndpoint = "https://6swxoe2skfglo4ee4kz6dalcra0crnpt.lambda-url.eu-west-2.on.aws/"

interface Event {
  id: string;
  venue: {
    name: string;
    city: string;
    country: string;
    latitude: string;
    longitude: string;
  };
  artist: {
    name: string;
  };
  datetime: string;
  offers: Array<{
    type: string;
    url: string;
    status: string;
  }>;
  lineup: string[];
}

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function BandsInTown() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch(AWSLambdaEndpoint);
        if (!response.ok) {
          throw new Error('Failed to fetch events');
        }
        const data = await response.json();
        setEvents(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const renderEvent = (event: Event) => {
    const date = new Date(event.datetime);
    const eventURL = event.offers.length > 0 ? event.offers[0].url : "#";
    const artistName = event.artist ? event.artist.name : "...";
    
    return (
      <motion.a 
        href={eventURL}
        target="_blank"
        rel="noopener noreferrer"
        key={event.id} 
        variants={item}
        whileHover={{ scale: 1.025 }}
        whileTap={{ scale: 0.98 }}
        className="flex gap-4 bg-zinc-900 p-4 rounded-lg hover:bg-zinc-800 transition-all my-2 block group"
      >
        <motion.div 
          className="text-center bg-zinc-800 p-3 rounded-lg group-hover:bg-zinc-700 transition-colors min-w-[80px] flex flex-col items-center justify-center"
          whileHover={{ scale: 1.05 }}
        >
          <Calendar className="w-6 h-6 mb-1 text-gray-400 group-hover:text-white transition-colors" />
          <div className="text-2xl font-bold">{date.getDate()}</div>
          <div className="text-sm text-gray-400">
            {date.toLocaleString('nl-NL', { month: 'short' })}
          </div>
        </motion.div>
        <motion.div 
          className="flex-1"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="text-2xl font-bold mb-0" style={{ marginTop: '7px' }}>{artistName}</h2>
          <h3 className="font-medium text-lg mb-1">{event.venue.name}</h3>
          <p className="text-sm text-gray-400 flex items-center gap-1">
            <MapPin size={14} className="text-gray-500" />
            {event.venue.city}, {event.venue.country}
          </p>
        </motion.div>
        <motion.div
          whileHover={{ x: 5 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
        >
          <ChevronRight className="w-6 h-6 text-gray-400 group-hover:text-white transition-colors self-center" />
        </motion.div>
      </motion.a>
    );
  };

  if (loading) return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="px-4 py-6"
    >
      <motion.h2 
        layout
        className="text-lg font-semibold mb-3"
      >
        Shows
      </motion.h2>
      <motion.div 
        variants={container}
        initial="hidden"
        animate="show"
        className="space-y-2"
      >
        {[1, 2, 3].map((_, index) => (
          <motion.div
            key={index}
            variants={item}
            className="h-20 bg-zinc-900 rounded-lg animate-pulse"
          />
        ))}
      </motion.div>
    </motion.div>
  );
  
  if (error) return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="px-4 py-6"
    >
      <motion.h2 
        layout
        className="text-lg font-semibold mb-3"
      >
        Shows
      </motion.h2>
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-red-500"
      >
        Error: {error}
      </motion.div>
    </motion.div>
  );

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="px-4 py-6"
    >
      <motion.h2 
        layout
        className="text-lg font-semibold mb-3"
      >
        Shows
      </motion.h2>
      <motion.div 
        variants={container}
        initial="hidden"
        animate="show"
        className="space-y-2"
      >
        <AnimatePresence>
          {events.length === 0 ? (
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-center py-4 text-gray-400"
            >
              Geen aankomende shows gevonden
            </motion.p>
          ) : (
            events.map(renderEvent)
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}
